import styles from './index.module.less';
import { App, Button } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/router';
import { orderSubsQueryRequest, orderSubsRequest } from '@/lib/service';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { OrderInfoModel, OrderModel } from '@/types/home';

import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import dynamic from 'next/dynamic';
import React from 'react';
import classNames from 'classnames';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';

const PlanItem = dynamic(() => import('./PlanItem'), { ssr: false });

const PlanContentDetail = ({
  title,
  desc,
  textColor = '#000',
  func,
}: {
  title?: string;
  desc?: string;
  textColor?: string;
  func: 'main' | 'story' | 'recent_follow' | 'hash_tag';
}) => {
  const planContainer = useRef(null);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const router = useRouter();
  const { media_name, avatar, media_url, type } = router.query;
  const { t } = useTranslation('common');
  const { message } = App.useApp();
  const [orderModel, setOrderModel] = useState<OrderModel>();
  const [loading, setLoading] = useState<boolean>(false);
  const renderCount = useRef(0);
  const totalComponents = 3; // 需要跟踪的组件总数
  const [allRendered, setAllRendered] = useState(false);

  const [queryParams, setQueryParams] = useState({
    media_name: media_name,
    avatar: avatar,
    media_url: media_url,
    type: type,
  });

  useEffect(() => {
    // 监听 query 参数变化
    setQueryParams({
      media_name: (router.query.media_name as string) || '',
      avatar: (router.query.avatar as string) || '',
      media_url: (router.query.media_url as string) || '',
      type: (router.query.type as string) || '',
    });
  }, [router.query]);

  const handleRenderComplete = () => {
    renderCount.current += 1;
    if (renderCount.current === totalComponents) {
      setAllRendered(true);
    }
  };

  const [orderList, setOrderList] = useState<OrderInfoModel[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<OrderInfoModel>();
  const { width } = useScreenSize();
  const [browserLanguage, setBrowserLanguage] = useState<string | null>(null);

  useEffect(() => {
    // 检查 navigator 对象是否存在于客户端
    if (typeof window !== 'undefined' && navigator.language) {
      // 获取浏览器的首选语言
      const browserLanguage = navigator.language;
      const prefix = browserLanguage.split('-')[0];
      // 设置语言前缀
      setBrowserLanguage(prefix ?? 'en');
    }
  }, []);

  useEffect(() => {
    // 当语言前缀不为空时，发送请求
    if (browserLanguage) {
      loadSubList(browserLanguage);
    }
  }, [browserLanguage]);

  const loadSubList = (language: string) => {
    setLoading(true);
    orderSubsQueryRequest(func, language)
      .then((res) => {
        if (res.code === 0) {
          const tempList = res?.data ?? [];
          // 获取 currency
          const currency = tempList ? tempList[0].currency : 'USD';
          const freeData: OrderInfoModel = {
            name: 'Free',
            amount: 0,
            item_id: -1,
            currency: currency,
          };
          const list = [freeData, ...tempList];
          setOrderList(list);
          setSelectedOrder(list?.length > 1 ? list[1] : list[0]);
        } else {
          message.error(res.message);
        }
      })
      .catch((error) => {
        message.error(error.message);
      })
      .finally(() => setLoading(false));
  };

  const orderSubscriteRequest = async (temp_item_id: number, num: number = 1) => {
    if (loading) {
      return;
    }
    setLoading(true);

    try {
      const res = await orderSubsRequest(
        {
          item_id: temp_item_id,
          media_name: queryParams?.media_name,
          avatar: queryParams?.avatar,
          media_url: queryParams?.media_url,
          num: num,
          redirect: '/dashboard?dashboard_type=toolkit',
        },
        router.locale
      );

      if (res.code === 0) {
        setOrderModel(res.data);
        ga_logEvent(EventName.Plan_Subscribe_Request_Success);
      } else {
        message.error(res.message);
        ga_logEvent(EventName.Plan_Subscribe_Request_Fail);
      }
      // setLoading(false);
    } catch (error: any) {
      message.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadScripePay = async () => {
    window.open(orderModel?.pay_link, '_self');
  };

  useEffect(() => {
    if (orderModel?.pay_link) {
      loadScripePay();
    } else {
      if (browserLanguage) {
        loadSubList(browserLanguage);
      }
    }
  }, [orderModel]);

  const containerScrollToCenter = () => {
    if (width < 900 && planContainer.current && orderList.length > 0 && selectedOrder != undefined && allRendered) {
      let dom = planContainer.current;
      if (dom && dom.children?.length > 0) {
        const cw = dom.children[1]?.scrollWidth * 3 + 45;
        const clw = dom.children[1]?.scrollWidth;
        const cliWidth = window.innerWidth;

        let tempIndex = 1;
        orderList.map((item, index) => {
          if (item?.item_id == selectedOrder?.item_id) {
            tempIndex = index;
          }
        });

        const tempLeft = tempIndex * (clw + 30) + clw / 2 - cliWidth / 2 + 15;
        if (tempLeft <= 0) {
          dom.scrollLeft = 0;
        } else if (tempLeft >= cw - clw) {
          dom.scrollLeft = cw - clw;
        } else {
          dom.scrollLeft = tempLeft;
        }
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      containerScrollToCenter();
    }, 50);
  }, [selectedOrder, allRendered, width]);

  const getTopContent = useCallback(() => {
    return (
      <div className={styles.topContainer} id="1">
        <h2
          style={{
            position: 'relative',
            display: 'inline-block',
            color: textColor,
          }}
        >
          {t('Pricing for Instagram Viewer & Downloader')}
        </h2>

        <p className={styles.desc} style={{ color: textColor }}>
          {t(
            `You can keep up with what's happening on Instagram while maintaining your anonymity. With Dolphin Radar, you can effortlessly view profiles, stories, and highlights in full privacy. At the same time, the person will not even know about it. The service also allows you to download the content you are interested in.`
          )}
        </p>

        <div className={styles.btnsContainer}>
          {['Free', 'Monthly', 'Annual'].map((item, index) => {
            const tempItem = orderList?.length > index ? orderList[index] : null;

            return (
              <Button
                key={item}
                className={classNames(styles.btn, tempItem?.item_id === selectedOrder?.item_id ? styles.active : '')}
                onClick={() => {
                  setSelectedOrder(tempItem);
                }}
              >
                {t(item)}
              </Button>
            );
          })}
        </div>

        {type != undefined && (
          <div className={styles.backContainer} onClick={() => router.back()}>{`< ${t('Back')}`}</div>
        )}
      </div>
    );
  }, [title, desc, textColor, type, selectedOrder, setSelectedOrder]);

  const getPlanContent = () => {
    return (
      <div className={classNames(styles.planContainer)} ref={planContainer}>
        {(orderList ?? [{}, {}]).map((item, index) => {
          return (
            <PlanItem
              isLast={index === orderList.length - 1}
              onRenderComplete={handleRenderComplete}
              key={item?.item_id}
              selectedItem={selectedOrder}
              orderInfo={item}
              onConfirmClick={(num: number) => {
                if (!isLogin) {
                  onChangedLoginView(false);
                  onOpenLoginModal(true);
                  return;
                }

                if (item?.amount === 0) {
                  return;
                }

                ga_logEvent(EventName.GA_plan_subscribe_clicks);
                orderSubscriteRequest(item.item_id, num);
                ga_logEvent(EventName.begin_checkout, {
                  currency: item.currency,
                  value: item.amount,
                  items: [
                    {
                      item_id: item?.item_id,
                      item_name: item?.name,
                      price: item.amount,
                      quantity: num,
                    },
                  ],
                });
              }}
              onDivClick={() => {
                setSelectedOrder(item);
              }}
            />
          );
        })}
      </div>
    );
  };

  const contentContainer = () => {
    return (
      <div className={styles.content}>
        {getTopContent()}
        {getPlanContent()}
      </div>
    );
  };

  return <>{contentContainer()}</>;
};

const LandingPagePricingContent = ({
  title,
  desc,
  textColor,
  func,
}: {
  title?: string;
  desc?: string;
  textColor?: string;
  func: 'main' | 'story' | 'recent_follow' | 'hash_tag';
}) => {
  return <PlanContentDetail title={title} desc={desc} textColor={textColor} func={func} />;
};

export default LandingPagePricingContent;
